import {useRunningTimesOffPeriodsWithoutDetailCount} from '@domain/running-times'
import {IssueGroupingKey, useStoppageCodeStats} from '@domain/stoppages'
import {useInvertedSurface} from '@hconnect/common/styles'
import {StoppageKind} from '@hconnect/common/types'
import {customThemeConstants, PageContainer} from '@hconnect/uikit/src/lib2'
import {getPlantName, useUpmPlantsContext, useUpmPlantTimezone} from '@hconnect/upm'
import {Grid, Typography} from '@mui/material'
import {
  RcfaCompletionContainer,
  StoppageCategoryCountsChartContainer,
  StoppagesFailureChartContainer,
  useRcfaAvailability
} from '@widgets/downtime-analytics'
import moment from 'moment-timezone'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {StatusPageHeader} from '../components'
import {TopIssues} from '../components/downtime'
import {
  DOWNTIME_STATISTICS_PATH,
  DOWNTIME_STOPPAGES_PATH,
  DowntimeAnalyticsEventName,
  FEATURE_NAMESPACE
} from '../consts'
import {NewStoppagesBannerContainer} from '../containers'
import {useSortedStoppages, useTimeRangeParam, useTrackDowntimeDashboardAnalytics} from '../hooks'
import {useDowntimePlantId} from '../providers'
import {calculateEndDate, createUrl, getTimeStep, UrlFilterParams} from '../utils'

type DownTimeDashboardProps = {
  onGoBackToMOL?: () => void
  stoppageListHref?: string
  stoppageListTo?: string
  useParentRelativePaths?: boolean
}

export const DownTimeDashboard = ({
  onGoBackToMOL,
  stoppageListHref,
  stoppageListTo,
  useParentRelativePaths = false
}: DownTimeDashboardProps) => {
  const [isPageLoading, setIsPageLoading] = useState(true)
  const plantId = useDowntimePlantId()

  const {t} = useTranslation(FEATURE_NAMESPACE)

  const upmPlants = useUpmPlantsContext()
  const inverted = useInvertedSurface()
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const startOfToday = moment.utc(new Date()).tz(timezone).startOf('day')
  const timeRange = {
    from: startOfToday.clone().subtract(12, 'month').startOf('month').toDate(),
    to: startOfToday.clone().endOf('day').toDate()
  }

  const [, setTimeRange] = useTimeRangeParam()
  const navigate = useNavigate()
  const localTimeRange = `[${timeRange.from.toJSON()},${timeRange.to.toJSON()}]`
  const downtimeStoppagesPath = useParentRelativePaths
    ? `../${DOWNTIME_STOPPAGES_PATH}`
    : DOWNTIME_STOPPAGES_PATH
  const downtimeStatisticsPath = useParentRelativePaths
    ? `../${DOWNTIME_STATISTICS_PATH}`
    : DOWNTIME_STATISTICS_PATH

  useEffect(() => {
    // TODO: check if this can be removed now as config service is wrapping all routes
    localTimeRange && setTimeRange(localTimeRange)
    setIsPageLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTimeRange])

  const rcfaAvailabilityData = useRcfaAvailability({
    plantId,
    from: timeRange.from.toISOString(),
    to: timeRange.to.toISOString()
  })
  const timeStep = getTimeStep(timeRange, timezone)

  const {
    topIssuesSortingKey,
    setTopIssuesSortingKey,
    topIssuesGroupingKey,
    setTopIssuesGroupingKey,
    sortedStoppages,
    aggregatedStoppagesQuery
  } = useSortedStoppages({
    plantId,
    groupingEventName: DowntimeAnalyticsEventName.UserSet1LevelGrouping,
    sortingEventName: DowntimeAnalyticsEventName.UserSet1LevelSorting,
    timeRange,
    selectedStoppages: [StoppageKind.Incident, StoppageKind.Circumstance],
    timeStep
  })
  const {data: stoppageCodeStats = {}} = useStoppageCodeStats(
    plantId,
    {
      ...timeRange,
      timeStep,
      groupBy: topIssuesGroupingKey
    },
    {enabled: topIssuesGroupingKey !== IssueGroupingKey.STOPPAGE_CODE}
  )

  const stoppagesWithoutDetailTimeRange = {
    from: startOfToday.clone().subtract(3, 'month').toDate(),
    to: startOfToday.clone().endOf('day').toDate()
  }
  const {data: newStoppagesCount, isLoading: stoppagesCountIsInitialLoading} =
    useRunningTimesOffPeriodsWithoutDetailCount(plantId, {
      from: stoppagesWithoutDetailTimeRange.from.toJSON(),
      to: stoppagesWithoutDetailTimeRange.to.toJSON()
    })
  const hasNewStoppages = !!newStoppagesCount

  if (isPageLoading) return null

  return (
    <PageContainer>
      <StatusPageHeader
        title={t('label.header')}
        backButtonProps={
          onGoBackToMOL
            ? {
                targetName: t('goBackLabels.backToTarget', {
                  target: t('majorOutageLeadDashboard.pageTitle')
                }),
                onClick: onGoBackToMOL
              }
            : undefined
        }
        afterTitle={
          <Typography
            className={inverted ? 'text-foreground_inverted' : 'text-foreground'}
            sx={{
              pl: customThemeConstants().defaultGridSpacing
            }}
          >
            {getPlantName(upmPlants, plantId)}
          </Typography>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TopIssues
            timeRange={timeRange}
            title={t('label.topIssues')}
            issueCount={topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL ? undefined : 12}
            onBarClick={(groupId?: string) => {
              const filters: UrlFilterParams = {
                timeRange: localTimeRange,
                sortBy: topIssuesSortingKey,
                groupBy: topIssuesGroupingKey
              }
              if (topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL) {
                const start = moment.utc(groupId)
                const utcEndDate = calculateEndDate(timeStep, start.tz(timezone))
                const utcDateString = `[${start.toJSON()},${utcEndDate.toJSON()}]`
                filters.timeRange = utcDateString
              }
              if (topIssuesGroupingKey === IssueGroupingKey.MAIN_EQUIPMENT && groupId) {
                filters.mainEquipmentNumbers = [groupId]
              }
              if (topIssuesGroupingKey === IssueGroupingKey.EQUIPMENT_NUMBER && groupId) {
                filters.equipmentNumbers = [groupId]
              }
              if (topIssuesGroupingKey === IssueGroupingKey.STOPPAGE_CODE && groupId) {
                filters.stoppageCode = [groupId]
              }
              const url = createUrl(filters, downtimeStoppagesPath)
              trackAnalyticsEvent(DowntimeAnalyticsEventName.UserClicksOn1LevelStoppagesGraph, {})
              void navigate(url, {relative: 'path'})
            }}
            onClick={() => {
              const filters: UrlFilterParams = {
                timeRange: localTimeRange,
                sortBy: topIssuesSortingKey,
                groupBy: topIssuesGroupingKey
              }
              const url = createUrl(filters, downtimeStoppagesPath)
              trackAnalyticsEvent(DowntimeAnalyticsEventName.UserClicksOn1LevelStoppagesGraph, {})
              void navigate(url, {relative: 'path'})
            }}
            topIssuesSortingKey={topIssuesSortingKey}
            onTopIssuesSortingKeyChange={setTopIssuesSortingKey}
            topIssuesGroupingKey={topIssuesGroupingKey}
            onTopIssuesGroupingKeyChange={setTopIssuesGroupingKey}
            sortedStoppages={sortedStoppages}
            aggregatedStoppagesQuery={aggregatedStoppagesQuery}
            stoppageCodeStats={stoppageCodeStats}
            onShowKilnStatsClick={() => void navigate(downtimeStatisticsPath, {relative: 'path'})}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StoppageCategoryCountsChartContainer
            plantId={plantId}
            from={timeRange.from}
            to={timeRange.to}
          />
        </Grid>
        <Grid item sm={8} xs={12}>
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={6}>
              {rcfaAvailabilityData.data && (
                <RcfaCompletionContainer
                  rcfaData={rcfaAvailabilityData.data}
                  onClick={() =>
                    trackAnalyticsEvent(DowntimeAnalyticsEventName.UserClicksOnRcfa, {})
                  }
                  stoppageListHref={stoppageListHref}
                  stoppageListTo={stoppageListTo}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <StoppagesFailureChartContainer
                plantId={plantId}
                from={timeRange.from}
                to={timeRange.to}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          {/* TODO: check if this should be moved to isFetching */}
          {!stoppagesCountIsInitialLoading && hasNewStoppages && (
            <NewStoppagesBannerContainer newStoppagesCount={newStoppagesCount ?? 0} />
          )}
        </Grid>
      </Grid>
    </PageContainer>
  )
}
